<template>
  <div class="filter-group">
    <label class="filter-label">
      <v-icon small color="#023449" class="mr-1">mdi-file-document-outline</v-icon>
      Tipo de Lançamento
      <v-chip
          v-if="internalValue.tipos.length > 0"
          x-small
          color="rgba(2, 52, 73, 0.1)"
          text-color="#023449"
          class="ml-auto filter-badge"
      >
        {{ getTotalCount() }}
      </v-chip>
    </label>

    <!-- Seleção de Tipos -->
    <v-autocomplete
        v-model="internalValue.tipos"
        :items="items"
        item-text="tipo"
        item-value="codigo"
        label="Selecione os tipos"
        :menu-props="{ closeOnContentClick: true }"
        multiple dense outlined hide-details
        @change="handleTipoChange"
        color="#023449"
        class="custom-select"
        :disabled="loading || loadingStatus"
        :readonly="loading || loadingStatus"
        ref="tiposAutocomplete"
    >
      <template v-slot:selection="{ item, index }">
        <v-chip v-if="index < 2" small color="#023449" text-color="white" class="mr-1">
          {{ item.tipo }}
        </v-chip>
        <span v-if="index === 2" class="grey--text text-caption">
          (+{{ internalValue.tipos.length - 2 }})
        </span>
      </template>
      <template v-slot:item="{ item, on, attrs }">
        <v-list-item
            v-bind="attrs"
            v-on="loading ? {} : on"
            :disabled="loading"
            :class="{ 'item-disabled': loading }"
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.tipo }}</v-list-item-title>
            <v-list-item-subtitle>
              ID: {{ item.codigo }}
              <v-chip v-if="item.flagstatus" x-small color="#e8f5e9" text-color="#10b981" class="ml-2">
                Com Status
              </v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>

    <!-- Seleção de Status -->
    <v-autocomplete
        v-model="internalValue.status"
        :items="statusLancamento"
        item-text="status"
        item-value="codigo"
        label="Selecione o status"
        dense outlined hide-details
        @change="handleStatusChange"
        color="#023449"
        class="custom-select mt-3"
        :disabled="loading || loadingStatus || !hasStatusEnabled"
        :readonly="loading || loadingStatus || !hasStatusEnabled"
    >
      <template v-slot:selection="{ item }">
        <v-chip small :color="getStatusColor(item.codigo)" text-color="white">
          {{ item.status }}
        </v-chip>
      </template>
    </v-autocomplete>

    <div class="select-actions">
      <v-btn text x-small color="#023449" @click="selectAllTipos" :disabled="loading || loadingStatus">
        Selecionar Todos os Tipos
      </v-btn>
      <v-btn
          v-if="internalValue.tipos.length > 0"
          text x-small
          color="error"
          @click="clear"
          :disabled="loading || loadingStatus"
      >
        Limpar Tudo
      </v-btn>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {mapState} from "vuex";

export default {
  name: 'TipoLancamentoFilter',

  props: {
    value: {
      type: Object,
      required: true,
      default: () => ({ tipos: [], status: null })
    },
    items: { type: Array, required: true },
    loading: Boolean
  },

  data() {
    return {
      internalValue: {
        tipos: [...this.value.tipos],
        status: this.value.status || null
      },
      statusLancamento: [],
      initialized: false,
      loadingStatus: false
    };
  },

  computed: {
    ...mapState(["backendUrl"]),
    hasStatusEnabled() {
      if (!this.internalValue.tipos.length) return false;

      return this.internalValue.tipos.some(codigo => {
        const tipo = this.items.find(item => item.codigo === codigo);
        return tipo && tipo.flagstatus === true;
      });
    }
  },

  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newVal) {
        this.internalValue = {
          tipos: Array.isArray(newVal.tipos) ? [...newVal.tipos] : [],
          status: newVal.status || null
        };
      }
    },

    hasStatusEnabled: {
      immediate: false,
      handler(newVal, oldVal) {
        // Só reage após inicialização e quando houver mudança real
        if (!this.initialized) return;
        if (newVal === oldVal) return;

        // Quando habilitar status
        if (newVal && !oldVal) {
          // Seleciona automaticamente "TODOS" (código 1)
          this.internalValue.status = 1;
          this.emitChange();
        }

        // Quando desabilitar status
        if (!newVal && oldVal) {
          // Limpar seleção de status
          this.internalValue.status = null;
          this.emitChange();
        }
      }
    }
  },

  async mounted() {
    await this.loadStatusLancamento();
    this.initialized = true;

    // Verifica estado inicial após carregar status
    if (this.hasStatusEnabled && !this.internalValue.status) {
      this.internalValue.status = 1;
      this.emitChange();
    }
  },

  methods: {
    async loadStatusLancamento() {
      try {
        this.loadingStatus = true;

        const response = await axios.post(`${this.backendUrl}dashboard/rotativo/status/listar`, {});

        if (response.data && Array.isArray(response.data)) {
          this.statusLancamento = response.data;
        } else {
          this.statusLancamento = [];
        }
      } catch (error) {
        console.error('Erro ao carregar status:', error);
        this.statusLancamento = [];
      } finally {
        this.loadingStatus = false;
      }
    },

    getStatusColor(codigo) {
      if (codigo === 3) return '#10b981'; // "FINALIZADO"
      if (codigo === 2) return '#f59e0b'; // "PENDENTE"
      return '#023449'; // "TODOS"
    },

    emitChange() {
      this.$emit('input', {
        tipos: this.internalValue.tipos,
        status: this.internalValue.status
      });
      this.$emit('change');
    },

    handleTipoChange() {
      this.emitChange();

      // Fecha o menu após a seleção
      this.$nextTick(() => {
        if (this.$refs.tiposAutocomplete) {
          this.$refs.tiposAutocomplete.isMenuActive = false;
        }
      });
    },

    handleStatusChange() {
      this.emitChange();
    },

    getTotalCount() {
      const tiposCount = this.internalValue.tipos.length;

      if (!this.hasStatusEnabled || !this.internalValue.status) {
        return tiposCount;
      }

      // Se tem status selecionado, adiciona +1 no contador
      return `${tiposCount}+1`;
    },

    selectAllTipos() {
      if (this.loading || this.loadingStatus) return;
      this.internalValue.tipos = this.items.map(item => item.codigo);
      this.handleTipoChange();
    },

    clear() {
      if (this.loading || this.loadingStatus) return;
      this.internalValue = {
        tipos: [],
        status: null
      };
      this.emitChange();
    }
  }
};
</script>

<style scoped>
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(2, 52, 73, 0.05);
  transition: all 0.2s ease;
}

.filter-group:hover {
  border-color: #023449;
  box-shadow: 0 2px 4px rgba(2, 52, 73, 0.08);
}

.filter-label {
  display: flex;
  align-items: center;
  font-weight: 700;
  color: #023449;
  font-size: 0.938rem;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
}

.filter-label .v-chip {
  margin-left: auto;
  font-weight: 600;
}

.filter-badge {
  min-width: 28px;
  height: 22px;
  font-weight: 700;
  border-radius: 6px;
}

.custom-select >>> .v-input__control {
  min-height: 44px !important;
}

.custom-select >>> fieldset {
  border-color: #e5e7eb;
  border-width: 2px;
  transition: all 0.2s ease;
}

.custom-select >>> .v-input__slot {
  background: #ffffff;
  transition: all 0.2s ease;
}

.custom-select >>> .v-input__slot:hover fieldset {
  border-color: #023449;
}

.custom-select.v-input--is-disabled >>> fieldset {
  border-color: #e5e7eb !important;
}

.custom-select.v-input--is-disabled >>> .v-input__slot {
  cursor: not-allowed !important;
  background-color: #f9fafb !important;
}

.custom-select >>> .v-select__selections {
  min-height: 42px;
}

.custom-select >>> .v-chip {
  font-weight: 600;
  border-radius: 8px;
}

.item-disabled {
  pointer-events: none !important;
  opacity: 0.4 !important;
  background-color: #f8fafc !important;
  cursor: not-allowed !important;
}

.item-disabled .v-list-item__title,
.item-disabled .v-list-item__subtitle {
  color: #94a3b8 !important;
}

.select-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-top: 4px;
  flex-wrap: wrap;
}

.select-actions >>> .v-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  font-size: 0.813rem;
  padding: 0 12px !important;
  min-width: auto;
  transition: all 0.2s ease;
}

.select-actions >>> .v-btn:hover {
  transform: translateX(2px);
}
</style>